import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import undoRedoManager from '../../utils/UndoRedoManager';
import { ThreeMaterialPropertyCommand } from '../../utils/Command';
import {
  collectMaterialEntries,
  listPresetMaterialPanelSections,
  listShaderUniformDescriptors,
  serializeMaterialPropertyValue,
} from '../../utils/ThreeJsUtils/materialPropertyEditor';
import { setMaterialMapVisibility } from '../../utils/ThreeJsUtils/materialMaps';
import MaterialPropertyField from './MaterialPropertyField';

const MaterialSectionDropdown = ({ title, isOpen, onToggle, children }) => (
  <div className="border-b border-gray-800/80 last:border-b-0">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-1.5 px-1 py-1.5 text-left hover:bg-[#252525] transition-colors"
    >
      {isOpen ? (
        <ChevronDown size={12} className="text-gray-400 shrink-0" />
      ) : (
        <ChevronRight size={12} className="text-gray-400 shrink-0" />
      )}
      <span className="text-[9px] uppercase tracking-wide text-gray-400 font-medium">
        {title}
      </span>
    </button>
    {isOpen && <div className="pb-1.5 flex flex-col gap-0.5">{children}</div>}
  </div>
);

const MaterialSlotPresetPanel = React.memo(
  ({ entry, onPropertyChange, onTextureUpload, refreshKey }) => {
    const sections = useMemo(() => {
      if (!entry?.material) return [];
      return listPresetMaterialPanelSections(entry.material);
    }, [entry?.material, entry?.materialUuid, refreshKey]);

    const uniforms = useMemo(() => {
      if (!entry?.material) return [];
      return listShaderUniformDescriptors(entry.material).map((desc) => ({
        ...desc,
        label: desc.key.replace('uniform:', 'uniform.'),
      }));
    }, [entry?.material, entry?.materialUuid, refreshKey]);

    const [openSections, setOpenSections] = useState({});

    const toggleSection = useCallback((sectionId) => {
      setOpenSections((prev) => ({
        ...prev,
        [sectionId]: !prev[sectionId],
      }));
    }, []);

    if (sections.length === 0 && uniforms.length === 0) return null;

    return (
      <div className="border border-gray-800 rounded mb-2 overflow-hidden">
        <p className="text-[10px] text-gray-400 px-2 py-1 bg-[#1a1a1a] truncate">
          {entry.label}
          <span className="text-gray-600 ml-1">({entry.materialType})</span>
        </p>
        <div className="px-1 py-0.5 flex flex-col">
          {sections.map((section) => {
            const isOpen = !!openSections[section.id];
            return (
              <MaterialSectionDropdown
                key={`${entry.id}-${section.id}`}
                title={section.title}
                isOpen={isOpen}
                onToggle={() => toggleSection(section.id)}
              >
                {section.fields.map((desc) => (
                  <MaterialPropertyField
                    key={`${entry.id}-${desc.key}`}
                    descriptor={{ ...desc, key: desc.key }}
                    onChange={(propertyKey, nextPayload) =>
                      onPropertyChange(entry, propertyKey, nextPayload)
                    }
                    onTextureUpload={(mapKey, dataUrl) =>
                      onTextureUpload?.(entry, mapKey, dataUrl)
                    }
                    bottomPanelSelector="#timeline-panel"
                  />
                ))}
              </MaterialSectionDropdown>
            );
          })}
          {uniforms.length > 0 && (
            <MaterialSectionDropdown
              title="Shader Uniforms"
              isOpen={!!openSections.uniforms}
              onToggle={() => toggleSection('uniforms')}
            >
              {uniforms.map((desc) => (
                <MaterialPropertyField
                  key={`${entry.id}-${desc.key}`}
                  descriptor={desc}
                  onChange={(propertyKey, nextPayload) =>
                    onPropertyChange(entry, propertyKey, nextPayload)
                  }
                  bottomPanelSelector="#timeline-panel"
                />
              ))}
            </MaterialSectionDropdown>
          )}
        </div>
      </div>
    );
  }
);

/**
 * Preset Three.js material fields (values from object, edits apply back to material).
 */
const MaterialPresetFields = ({ selectedObject, onPanelSync }) => {
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const onMapsRestored = () => setRefreshKey((k) => k + 1);
    window.addEventListener('materialMapsRestored', onMapsRestored);
    return () => {
      window.removeEventListener('materialMapsRestored', onMapsRestored);
    };
  }, []);

  const materialEntries = useMemo(() => {
    if (!selectedObject) return [];
    return collectMaterialEntries(selectedObject);
  }, [selectedObject, refreshKey]);

  const handlePropertyChange = useCallback(
    (entry, propertyKey, nextPayload) => {
      const prevSerialized = serializeMaterialPropertyValue(
        entry.material,
        propertyKey
      );
      if (!prevSerialized) return;

      undoRedoManager.executeCommand(
        new ThreeMaterialPropertyCommand(
          entry,
          propertyKey,
          prevSerialized,
          nextPayload
        )
      );

      if (propertyKey === 'opacity' && nextPayload?.kind === 'number') {
        onPanelSync?.({ opacity: nextPayload.data, alpha: nextPayload.data });
      }
      if (propertyKey === 'transparent' && nextPayload?.kind === 'boolean') {
        onPanelSync?.({ transparency: nextPayload.data });
      }

      setRefreshKey((k) => k + 1);
    },
    [onPanelSync]
  );

  const handleTextureUpload = useCallback(
    (entry, mapKey, dataUrl) => {
      if (!entry?.mesh || !mapKey || !dataUrl) return;
      handlePropertyChange(entry, mapKey, { kind: 'texture', data: dataUrl });
      setMaterialMapVisibility(entry.mesh, mapKey, true);
      setRefreshKey((k) => k + 1);
    },
    [handlePropertyChange]
  );

  if (materialEntries.length === 0) return null;

  return (
    <div className="mt-2 pt-2 border-t border-gray-800 flex flex-col gap-1">
      <p className="text-[10px] text-gray-400 font-medium px-1">
        Material properties (from object)
      </p>
      {materialEntries.map((entry) => (
        <MaterialSlotPresetPanel
          key={entry.id}
          entry={entry}
          onPropertyChange={handlePropertyChange}
          onTextureUpload={handleTextureUpload}
          refreshKey={refreshKey}
        />
      ))}
    </div>
  );
};

export default React.memo(MaterialPresetFields);
